import {
  PencilLine,
  Trash2,
  User,
  Save,
  X,
  UserPlus,
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import React, { useState, useEffect } from "react";

const ManageUsers = () => {
  // State Management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  // Edit State
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    userName: "",
    UserEmail: "",
    userRole: "",
  });

  // UI State
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  // New User Form State
  const [newUserForm, setNewUserForm] = useState({
    userName: "",
    UserEmail: "",
    userRole: "Admin",
    profileURL: "",
  });
  const [savingNewUser, setSavingNewUser] = useState(false);

  const API_BASE = "https://ecomproductdes22-fe51f220f2a7.herokuapp.com";

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/users/admins`);
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const displaySuccessAlert = (message) => {
    setAlertMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  // --- Sorting & Filtering Logic ---
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredUsers = () => {
    let filteredUsers = [...users];
    if (searchTerm) {
      filteredUsers = filteredUsers.filter(
        (user) =>
          (user.userName &&
            user.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.UserEmail &&
            user.UserEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.userRole &&
            user.userRole.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (sortConfig.key) {
      filteredUsers.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filteredUsers;
  };

  const sortedAndFilteredUsers = getSortedAndFilteredUsers();
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedAndFilteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );
  const totalPages = Math.ceil(sortedAndFilteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  // --- Helper Functions ---
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  const getRoleBadgeStyle = (role) => {
    if (!role) return { backgroundColor: "#f1f3f5", color: "#495057" };
    switch (role.toLowerCase()) {
      case "admin":
        return {
          background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
          color: "#c92a2a",
          fontWeight: 600,
        };
      case "moderator":
        return { backgroundColor: "#fff3bf", color: "#e67700" };
      case "member":
        return { backgroundColor: "#d3f9d8", color: "#2b8a3e" };
      default:
        return { backgroundColor: "#f1f3f5", color: "#495057" };
    }
  };

  // --- Handlers ---
  const handleEditClick = (user) => {
    setEditingUser(user._id);
    setEditForm({
      userName: user.userName || "",
      UserEmail: user.UserEmail || "",
      userRole: user.userRole || "member",
    });
  };

  const handleCancelEdit = () => setEditingUser(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleSaveUser = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (!response.ok) throw new Error("Failed to update");
      const updatedUsers = users.map((user) =>
        user._id === userId ? { ...user, ...editForm } : user
      );
      setUsers(updatedUsers);
      setEditingUser(null);
      displaySuccessAlert("User updated successfully");
    } catch (err) {
      setError(`Failed to update user: ${err.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await fetch(`${API_BASE}/api/users/${userId}`, {
          method: "DELETE",
        });
        if (!response.ok) throw new Error("Failed to delete");
        setUsers(users.filter((user) => user._id !== userId));
        displaySuccessAlert("User deleted successfully");
      } catch (err) {
        setError(`Failed to delete user: ${err.message}`);
      }
    }
  };

  // --- New User Handlers ---
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm({ ...newUserForm, [name]: value });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSavingNewUser(true);
    setError(null);

    try {
      const payload = {
        userName: newUserForm.userName,
        userEmail: newUserForm.UserEmail,
        profileURL: newUserForm.profileURL || "https://placehold.co/100x100?text=User",
        userRole: newUserForm.userRole,
      };

      const response = await fetch(`${API_BASE}/api/admin/create-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to create user");
        }

        displaySuccessAlert("New admin user created successfully!");
        setShowAddModal(false);
        setNewUserForm({
          userName: "",
          UserEmail: "",
          userRole: "Admin",
          profileURL: "",
        });
        fetchUsers();
      } else {
        throw new Error("Server returned an invalid response.");
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setSavingNewUser(false);
    }
  };

  const getUserInitials = (user) => {
    if (user.userName) return user.userName.charAt(0).toUpperCase();
    if (user.UserEmail) return user.UserEmail.charAt(0).toUpperCase();
    return "?";
  };

  const getAvatarBgColor = (userId) => {
    const colors = ["#4263eb", "#0ca678", "#f76707", "#7048e8", "#e64980", "#1098ad"];
    if (!userId) return colors[0];
    const hash = userId.split("").reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    return colors[hash % colors.length];
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    let startPage = Math.max(currentPage - 2, 1);
    let endPage = Math.min(startPage + 4, totalPages);
    if (endPage - startPage < 4) startPage = Math.max(endPage - 4, 1);

    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination mb-0" style={{ gap: "5px" }}>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link rounded-circle border-0 text-muted" onClick={() => paginate(currentPage - 1)}>‹</button>
          </li>
          {pageNumbers.map((page) => (
            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
              <button
                className="page-link border-0 rounded-circle"
                style={{
                  width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center",
                  backgroundColor: currentPage === page ? "#4263eb" : "transparent",
                  color: currentPage === page ? "white" : "#495057",
                }}
                onClick={() => paginate(page)}
              >
                {page}
              </button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link rounded-circle border-0 text-muted" onClick={() => paginate(currentPage + 1)}>›</button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="container-fluid py-5" style={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        
        {/* Floating Alerts */}
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 1050, width: "300px" }}>
          {showSuccessAlert && (
            <div className="alert alert-success d-flex align-items-center shadow-lg border-0" role="alert" style={{ borderRadius: "12px" }}>
              <CheckCircle size={20} className="me-2" />
              <div>{alertMessage}</div>
            </div>
          )}
          {error && (
            <div className="alert alert-danger d-flex align-items-center shadow-lg border-0" role="alert" style={{ borderRadius: "12px" }}>
              <AlertCircle size={20} className="me-2" />
              <div style={{ fontSize: "14px" }}>{error}</div>
              <button type="button" className="btn-close ms-auto" onClick={() => setError(null)}></button>
            </div>
          )}
        </div>

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: "#212b36" }}>User Management</h2>
            <p className="text-muted mb-0">Manage administrators and roles.</p>
          </div>
          <button
            className="btn d-flex align-items-center gap-2 shadow-sm"
            style={{ backgroundColor: "#4263eb", color: "white", borderRadius: "10px", padding: "10px 20px", fontWeight: 500, border: "none" }}
            onClick={() => setShowAddModal(true)}
          >
            <UserPlus size={18} /> Add New Admin
          </button>
        </div>

        {/* Main Card */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
          {/* Toolbar */}
          <div className="card-header bg-white border-0 py-4 px-4">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h5 className="mb-0 fw-bold" style={{ color: "#212b36" }}>Admin Directory</h5>
              </div>
              <div className="col-md-6 d-flex justify-content-end align-items-center gap-3">
                <div className="input-group" style={{ maxWidth: "300px" }}>
                  <span className="input-group-text bg-white border-end-0"><Search size={16} style={{ color: "#bdbdbd" }} /></span>
                  <input
                    type="text" className="form-control border-start-0" placeholder="Search users..."
                    value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ paddingLeft: "0" }}
                  />
                </div>
                <button className="btn btn-white border shadow-sm d-flex align-items-center gap-1" onClick={fetchUsers} style={{ borderRadius: "8px", color: "#637381" }}>
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr style={{ backgroundColor: "#f4f6f8" }}>
                      <th className="py-3 px-4 border-0" style={{ color: "#637381", fontSize: "13px", fontWeight: "600", cursor: "pointer" }} onClick={() => requestSort("userName")}>USER {getSortIndicator("userName")}</th>
                      {/* CREDITS REMOVED */}
                      <th className="py-3 px-4 border-0" style={{ color: "#637381", fontSize: "13px", fontWeight: "600" }}>ROLE</th>
                      {/* MEMBERSHIP REMOVED */}
                      <th className="py-3 px-4 border-0 text-center" style={{ color: "#637381", fontSize: "13px", fontWeight: "600", cursor: "pointer" }} onClick={() => requestSort("createdAt")}>JOINED {getSortIndicator("createdAt")}</th>
                      <th className="py-3 px-4 border-0 text-end" style={{ color: "#637381", fontSize: "13px", fontWeight: "600" }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.length === 0 ? (
                      <tr><td colSpan="4" className="text-center py-5"><div className="text-muted"><User size={32} className="mb-2 opacity-50" /><p className="mb-0">No users found.</p></div></td></tr>
                    ) : (
                      currentUsers.map((user) => (
                        <tr key={user._id} className="border-top" style={{ borderColor: "#f4f6f8" }}>
                          {editingUser === user._id ? (
                            <>
                              <td className="px-4 py-3" style={{ minWidth: "250px" }}>
                                <input type="text" className="form-control form-control-sm mb-1" name="userName" value={editForm.userName} onChange={handleInputChange} />
                                <input type="email" className="form-control form-control-sm" name="UserEmail" value={editForm.UserEmail} onChange={handleInputChange} />
                              </td>
                              {/* Credits Input Removed */}
                              <td className="px-4 py-3">
                                <select className="form-select form-select-sm" name="userRole" value={editForm.userRole} onChange={handleInputChange}>
                                  <option value="member">Member</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </td>
                              {/* Membership Input Removed */}
                              <td className="px-4 py-3 text-muted text-center">{formatDate(user.createdAt)}</td>
                              <td className="px-4 py-3 text-end">
                                <button className="btn btn-sm btn-light border me-1" onClick={() => handleSaveUser(user._id)}><Save size={14} className="text-success" /></button>
                                <button className="btn btn-sm btn-light border" onClick={handleCancelEdit}><X size={14} /></button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="avatar me-3" style={{ backgroundColor: getAvatarBgColor(user._id), color: "white", width: "42px", height: "42px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px" }}>
                                    {getUserInitials(user)}
                                  </div>
                                  <div>
                                    <div className="fw-semibold" style={{ color: "#212b36" }}>{user.userName || "N/A"}</div>
                                    <div className="small text-muted">{user.UserEmail || "N/A"}</div>
                                  </div>
                                </div>
                              </td>
                              {/* Credits Display Removed */}
                              <td className="px-4 py-3">
                                <span className="badge rounded-pill px-3 py-1" style={getRoleBadgeStyle(user.userRole)}>{user.userRole || "Member"}</span>
                              </td>
                              {/* Membership Display Removed */}
                              <td className="px-4 py-3 text-muted text-center">{formatDate(user.createdAt)}</td>
                              <td className="px-4 py-3 text-end">
                                <div className="btn-group">
                                  <button className="btn btn-sm btn-white border-0 text-muted" onClick={() => handleEditClick(user)} title="Edit"><PencilLine size={16} /></button>
                                  <button className="btn btn-sm btn-white border-0 text-danger" onClick={() => handleDeleteUser(user._id)} title="Delete"><Trash2 size={16} /></button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {!loading && (
            <div className="card-footer bg-white border-0 py-3 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                  Showing <strong>{indexOfFirstUser + 1}</strong> - <strong>{Math.min(indexOfLastUser, sortedAndFilteredUsers.length)}</strong> of <strong>{sortedAndFilteredUsers.length}</strong>
                </div>
                {renderPagination()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <div>
                  <h5 className="mb-1 fw-bold">Create New Admin</h5>
                  <p className="text-muted small mb-0">Fill in the details to add a new user.</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="modal-body px-4 py-4">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label text-muted small">Full Name</label>
                      <input type="text" className="form-control" name="userName" value={newUserForm.userName} onChange={handleNewUserChange} required style={{ borderRadius: "8px" }} />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small">Email Address</label>
                      <input type="email" className="form-control" name="UserEmail" value={newUserForm.UserEmail} onChange={handleNewUserChange} required style={{ borderRadius: "8px" }} />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small">Role</label>
                      <select className="form-select" name="userRole" value={newUserForm.userRole} onChange={handleNewUserChange} style={{ borderRadius: "8px" }}>
                        <option value="Admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                    </div>
                    {/* Membership Select Removed */}
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button type="button" className="btn btn-light" style={{ borderRadius: "8px" }} onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" style={{ borderRadius: "8px", backgroundColor: "#4263eb", borderColor: "#4263eb" }} disabled={savingNewUser}>
                    {savingNewUser ? "Creating..." : <><UserPlus size={18} /> Create User</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;